<template>
  <div class="space-y-6">
    <div class="flex flex-col gap-5">
      <!-- Fila A: campaña -->
      <div class="flex items-start gap-4">
        <!-- <div
          class="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--p-primary-color)] text-white text-sm font-semibold flex-shrink-0"
        >
          A
        </div> -->

        <div class="flex-1">
          <label
            class="block text-sm font-medium mb-2 text-[var(--p-primary-color)] dark:text-[var(--p-text-color)]"
          >
            A. {{ t('whatsapp.select_campaign_contacts.select_campaign') }}
          </label>

          <AppSelect
            :model-value="selectedCampaignId"
            :options="campaignOptions"
            :placeholder="t('whatsapp.select_campaign_contacts.campaign_placeholder')"
            :show-clear="true"
            :error-message="campaignValidationMessage"
            :is-disabled="isLoadingCampaigns"
            @update:model-value="handleCampaignChange"
            :editable="false"
          />
        </div>
      </div>

      <!-- Fila B: contactos -->
      <div class="flex items-start gap-4">
        <!-- <div
          class="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--p-primary-color)] text-white text-sm font-semibold flex-shrink-0"
        >
          B
        </div> -->

        <div class="flex-1">
          <label
            class="block text-sm font-medium mb-2 text-[var(--p-primary-color)] dark:text-[var(--p-text-color)]"
          >
            B. {{ t('whatsapp.select_campaign_contacts.select_contacts') }}
          </label>

          <div class="space-y-3">
            <div class="flex justify-between mt-2 items-center gap-2">
              <div class="flex gap-8">
                <div v-if="!singleSelection" class="flex items-center pl-1 gap-2">
                  <Checkbox
                    v-model="selectAllContacts"
                    binary
                    size="small"
                    input-id="select-all"
                    :disabled="availableContacts.length === 0 || isSelectingAllContacts"
                    @change="handleSelectAllChange"
                  />

                  <label
                    for="select-all"
                    class="text-xs cursor-pointer text-[var(--p-primary-color)] dark:text-[var(--p-text-color)]"
                  >
                    {{ t('whatsapp.select_campaign_contacts.select_all') }}
                  </label>
                </div>

                <div class="flex gap-2 items-center">
                  <IconPersons
                    width="18"
                    height="18"
                    class="text-[var(--p-primary-color)]"
                  ></IconPersons>
                  <label class="text-xs text-[var(--p-primary-color)]">
                    {{ selectedNonManualContactsCount }}/{{ contactsTotal }}
                  </label>
                </div>
              </div>

              <div v-if="selectedCampaign">
                <AutoComplete
                  v-model="contactSearchQuery"
                  :suggestions="filteredContacts"
                  option-label="label"
                  :placeholder="t('whatsapp.search_contacts')"
                  size="small"
                  input-class="!w-full !rounded-lg !text-xs  py-2! !text-surface-100 bg-transparent! shadow-sm! !border-white/60 dark:!border-[var(--p-primary-color)]/40 hover:!border-white dark:hover:!border-[var(--p-primary-color)]/60 hover:!shadow-md"
                  class="w-full"
                  fluid
                  :loading="isLoadingContacts"
                  @complete="searchContacts"
                  @item-select="handleContactSelect"
                >
                  <template #option="slotProps">
                    <div class="flex items-center gap-1.5 py-0 text-xs leading-tight">
                      <User :size="12" />
                      <div class="flex min-w-0 flex-col">
                        <span class="truncate text-xs dark:text-[var(--p-text-color)]">
                          {{ slotProps.option.label }}
                        </span>
                        <span
                          class="truncate text-xs leading-tight text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
                        >
                          {{ displayContactPhone(slotProps.option) }}
                        </span>
                      </div>
                    </div>
                  </template>

                  <template #empty>
                    <span
                      class="block px-2 py-1 text-xs leading-tight text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
                    >
                      {{ t('whatsapp.no_contacts_found') }}
                    </span>
                  </template>
                </AutoComplete>
              </div>
            </div>

            <div class="dark:bg-transparent! backdrop-blur-sm! min-h-40 shadow-sm max-h-50 overflow-y-auto rounded-lg p-2">
              <div v-if="displayedContacts.length" class="grid gap-2 pl-2">
                <button
                  v-for="contact in displayedContacts"
                  :key="contact.id"
                  type="button"
                  class="flex items-center justify-between rounded-xl text-left transition-all duration-200"
                  @click="toggleContactSelection(contact)"
                >
                  <div class="flex min-w-0 items-center cursor-pointer gap-3">
                    <Checkbox
                      :model-value="isContactSelected(contactKey(contact))"
                      size="small"
                      binary
                      :disabled="true"
                      inputId="size_small"
                    />
                    <div class="min-w-0">
                      <p
                        class="truncate text-xs text-[var(--p-primary-color)] font-medium dark:text-[var(--p-text-color)]"
                      >
                        {{ contact.name || t('whatsapp.select_campaign_contacts.unnamed_contact') }}
                      </p>
                      <p
                        class="truncate text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
                      >
                        {{ displayContactPhone(contact) }}
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              <div
                v-else
                class="flex min-h-40 max-h-72 rounded-xl border border-dashed border-[var(--p-surface-300)] p-4 justify-center items-center text-sm text-[var(--p-surface-500)] dark:border-[var(--p-surface-700)] dark:text-[var(--p-surface-400)]"
              >
                {{
                  selectedCampaign
                    ? t('whatsapp.no_contacts_available')
                    : t('whatsapp.campaign_selection_required')
                }}
              </div>
            </div>

            <div class="flex items-center justify-between">
              <AppButtonIcon
                :label="$t('whatsapp.add_contact')"
                severity="secondary"
                size="small"
                class="add-manual-contact-button whitespace-nowrap bg-none! hover-none! w-50 shadow-sm"
                button-class="text-[var(--p-primary-color)] disabled:!cursor-not-allowed "
                :disabled="isAddManualContactDisabled"
                @click="handleAddManualContactClick"
              >
                <template #icon>
                  <Plus :size="16" />
                </template>
              </AppButtonIcon>

              <div
                v-if="selectedCampaign && contactsTotalPages > 1"
                class="flex items-center gap-2"
              >
                <button
                  type="button"
                  class="flex items-center justify-center cursor-pointer rounded-md p-1 text-[var(--p-primary-color)] transition-colors disabled:cursor-not-allowed disabled:opacity-30 dark:text-[var(--p-text-color)]"
                  :disabled="!canGoPreviousContactsPage"
                  @click="loadContactsPage(contactsPage - 1)"
                >
                  <ChevronLeft :size="16" />
                </button>

                <span class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]">
                  {{ contactsPage }}/{{ contactsTotalPages }}
                </span>

                <button
                  type="button"
                  class="flex items-center justify-center cursor-pointer rounded-md p-1 text-[var(--p-primary-color)] transition-colors disabled:cursor-not-allowed disabled:opacity-30 dark:text-[var(--p-text-color)]"
                  :disabled="!canGoNextContactsPage"
                  @click="loadContactsPage(contactsPage + 1)"
                >
                  <ChevronRight :size="16" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Fila C: seleccionados -->
      <!-- <div v-if="selectedContacts.length > 0" class="flex items-start gap-4">
        <div
          class="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--p-primary-color)] text-white text-sm font-semibold flex-shrink-0"
        >
          C
        </div>

        <div class="flex-1 space-y-2">
          <div class="flex items-center justify-between">
            <label class="block text-sm font-semibold dark:text-[var(--p-text-color)]">
              Contactos Seleccionados ({{ selectedContacts.length }})
            </label>

            <Button
              label="Limpiar todo"
              text
              size="small"
              severity="danger"
              @click="clearAllContacts"
            />
          </div>

          <div
            class="flex flex-wrap gap-1.5 p-3 border border-[var(--p-surface-200)] dark:border-[var(--p-surface-700)] rounded-lg bg-[var(--p-surface-50)] dark:bg-[var(--p-surface-900)]"
          >
            <Chip
              v-for="contact in selectedContacts"
              :key="contactKey(contact)"
              :label="contact.name || displayContactPhone(contact)"
              removable
              class="!text-xs !py-0.5 !px-2 !h-6"
              @remove="removeContact(contactKey(contact))"
            >
              <template #icon>
                <Phone v-if="contact.is_manual" :size="12" class="mr-1.5" />
                <User v-else :size="12" class="mr-1.5" />
              </template>
            </Chip>
          </div>
        </div>
      </div> -->

      <label
        v-if="selectedContacts.length === 0"
        severity="info"
        :closable="false"
        size="small"
        class="text-xs"
      >
        {{ t('whatsapp.contact_selection_label') }}
      </label>
    </div>
  </div>

  <AddManualContactsDialog
    v-model:visible="showManualContactsDialog"
    :campaign-name="selectedCampaign?.name ?? null"
    :campaign-id="selectedCampaign?.id ?? null"
    @save="handleManualContactsSave"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { isAxiosError } from 'axios'
import AutoComplete from 'primevue/autocomplete'
import Checkbox from 'primevue/checkbox'
import { User, Plus, ChevronLeft, ChevronRight } from 'lucide-vue-next'
import AppButtonIcon from '@/components/atoms/buttons/AppButtonIcon.vue'
import AppSelect from '@/components/atoms/selects/AppSelect.vue'
import type { SelectOption } from '@/components/atoms/selects/types/select-option.types'
import AddManualContactsDialog from './AddManualContactsDialog.vue'
import type { ContactSelection } from '@/components/whatsapp/types/interfaces'
import type { IOptionsCampaign } from '@/services/campaign/interfaces/options-campaign.interface'
import { TypeCampaign } from '@/services/campaign/enums/type-campaign.enum'
import { useContactService } from '@/services/contact/useContactService'
import type { ContactDto } from '@/services/contact/interfaces/contact.interface'
import type { ContactListItemDto } from '@/services/contact/interfaces/contact-list-item.interface'
import IconPersons from '@/assets/svg/persons_icon.svg?component'
import { useI18n } from 'vue-i18n'
import type { IPaginationMeta } from '@/services/interfaces/pagination-response.interface'
import { contactKey, formatUsername } from '@/components/whatsapp/services/contactIdentity'

/** Contacto al que se le puede enviar: por teléfono o, si no lo tiene, por BSUID. */
const isReachable = (contact: { phone?: string | null; bsuid?: string | null }) =>
  Boolean(contact.phone || contact.bsuid)

interface ContactOption {
  id: string
  /** Vacío si el contacto solo tiene BSUID. */
  phone: string
  bsuid?: string | null
  username?: string | null
  name?: string
  label: string
  custom_fields?: ContactSelection['custom_fields']
}

interface Props {
  campaigns: IOptionsCampaign[]
  defaultCampaignId?: string
  initialContact?: ContactSelection
  createInitialContactOnCampaignSelect?: boolean
  isLoadingCampaigns?: boolean
  singleSelection?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  createInitialContactOnCampaignSelect: false,
  isLoadingCampaigns: false,
  singleSelection: false,
})
const { t } = useI18n()
const { createContact, getContacts, searchContact } = useContactService()
const emit = defineEmits<{
  'update:contacts': [contacts: ContactSelection[]]
  'update:campaign': [campaign: IOptionsCampaign | null]
  'load-contacts': [campaignId: string]
  'manual-dialog-visibility': [visible: boolean]
}>()

const selectedCampaign = ref<IOptionsCampaign | null>(null)
const selectAllContacts = ref(false)
const contactSearchQuery = ref('')
const showManualContactsDialog = ref(false)
const selectedContacts = ref<ContactSelection[]>([])
const availableContacts = ref<ContactOption[]>([])
const filteredContacts = ref<ContactOption[]>([])
const isLoadingContacts = ref(false)
const isSelectingAllContacts = ref(false)
const selectedAllContactKeys = ref<Set<string>>(new Set())
let latestSearchRequest = 0

const contactsPage = ref(1)
const contactsPageSize = 10  //Cantidad de conactos a mostrar por pagina
const contactsPaginationMeta = ref<IPaginationMeta | null>(null)

let latestContactsPageRequest = 0
let latestSelectAllRequest = 0

const contactsTotal = computed(
  () => contactsPaginationMeta.value?.total ?? availableContacts.value.length,
)

const contactsTotalPages = computed(() => contactsPaginationMeta.value?.pages ?? 1)

const canGoPreviousContactsPage = computed(() => contactsPage.value > 1 && !isLoadingContacts.value)

const canGoNextContactsPage = computed(() => {
  const meta = contactsPaginationMeta.value
  if (!meta || isLoadingContacts.value) return false

  return meta.hasNextPage ?? contactsPage.value < meta.pages
})

const loadContactsPage = async (page = contactsPage.value) => {
  if (!selectedCampaign.value) {
    contactsPage.value = 1
    contactsPaginationMeta.value = null
    setAvailableContacts([])
    return
  }

  const requestId = ++latestContactsPageRequest
  isLoadingContacts.value = true

  try {
    const response = await getContacts({
      campaign_id: selectedCampaign.value.id,
      page,
      size: contactsPageSize,
    })

    if (requestId !== latestContactsPageRequest) return

    contactsPage.value = response.meta.page
    contactsPaginationMeta.value = response.meta

    setAvailableContacts(response.data.filter(isReachable).map(toContactOption))
  } catch {
    if (requestId === latestContactsPageRequest) {
      contactsPaginationMeta.value = null
      setAvailableContacts([])
    }
  } finally {
    if (requestId === latestContactsPageRequest) {
      isLoadingContacts.value = false
    }
  }
}

// -----------------------

const campaignValidationMessage = computed(() => {
  if (props.isLoadingCampaigns || selectedCampaign.value) return ''

  return t('whatsapp.campaing_requiered')
})

const getCampaignTypeLabel = (type: TypeCampaign): string => {
  const labels: Record<TypeCampaign, string> = {
    [TypeCampaign.BLASTING]: 'Blasting',
    [TypeCampaign.INCOMING]: t('whatsapp.select_campaign_contacts.incoming'),
    [TypeCampaign.OUTGOING]: t('whatsapp.select_campaign_contacts.outgoing'),
    [TypeCampaign.WHATSAPP]: 'WhatsApp',
    [TypeCampaign.PREVIEW]: 'Preview',
  }
  return labels[type] || type
}

const campaignOptions = computed<SelectOption[]>(() =>
  whatsappCampaigns.value.map((campaign) => ({
    name: `${campaign.name} - ${getCampaignTypeLabel(campaign.type)}`,
    value: campaign.id,
  })),
)

const selectedCampaignId = computed(() => selectedCampaign.value?.id ?? null)
const isAddManualContactDisabled = computed(() => !selectedCampaign.value)
const selectedContactKeys = computed(
  () => new Set(selectedContacts.value.map((contact) => contactKey(contact))),
)
const selectedNonManualContactsCount = computed(() => {
  if (selectAllContacts.value && contactsTotal.value > 0) return contactsTotal.value

  return selectedContacts.value.filter((contact) => !contact.is_manual).length
})

const maskPhoneNumber = (phone: string): string => {
  if (!selectedCampaign.value?.masked_number) return phone

  return phone.slice(0, 4) + '*'.repeat(Math.max(0, phone.length - 4))
}

/**
 * Teléfono (enmascarado según la campaña) y `@username`, los que tenga el
 * contacto; un aviso si no tiene ninguno de los dos.
 */
const displayContactPhone = (contact: {
  phone?: string | null
  username?: string | null
}): string =>
  [contact.phone ? maskPhoneNumber(contact.phone) : '', formatUsername(contact.username)]
    .filter(Boolean)
    .join(' · ') || t('whatsapp.select_campaign_contacts.whatsapp_only')

const buildContactLabel = (contact: {
  name?: string
  phone?: string | null
  username?: string | null
}): string =>
  `${contact.name || displayContactPhone(contact)} (${displayContactPhone(contact)})`

const displayedContacts = computed(() => {
  const query = contactSearchQuery.value.trim().toLowerCase()

  if (!query) return availableContacts.value

  return availableContacts.value.filter(
    (contact) => contact.label.toLowerCase().includes(query) || contact.phone.includes(query),
  )
})

const whatsappCampaigns = computed(() =>
  props.campaigns.filter((campaign) => campaign.type === TypeCampaign.WHATSAPP),
)

const handleCampaignChange = async (campaignId: string | number | boolean | null) => {
  const normalizedCampaignId = typeof campaignId === 'boolean' ? null : campaignId
  const normalizedCampaignIdString =
    normalizedCampaignId === null ? null : String(normalizedCampaignId)

  if (selectedCampaign.value?.id === normalizedCampaignIdString) return

  selectedCampaign.value =
    normalizedCampaignIdString === null
      ? null
      : (whatsappCampaigns.value.find((campaign) => campaign.id === normalizedCampaignIdString) ??
        null)

  selectedContacts.value = []
  selectAllContacts.value = false
  selectedAllContactKeys.value = new Set()
  availableContacts.value = []
  filteredContacts.value = []
  contactSearchQuery.value = ''

  emit('update:campaign', selectedCampaign.value)

  if (selectedCampaign.value) {
    await loadContactsPage(1)
    await ensureInitialContactInSelectedCampaign()
  }
}

const buildInitialContactSelection = (
  contact: ContactDto,
  fallbackCustomFields: ContactSelection['custom_fields'],
): ContactSelection => ({
  id: contact.id,
  phone: contact.phone,
  bsuid: contact.bsuid,
  username: contact.username,
  name: contact.name,
  campaign_id: contact.campaign_id,
  is_manual: false,
  custom_fields: contact.custom_fields?.length ? contact.custom_fields : fallbackCustomFields,
})

const addSelectedInitialContact = (contact: ContactSelection) => {
  const contactSelection: ContactSelection = {
    ...contact,
    campaign_id: selectedCampaign.value?.id ?? contact.campaign_id,
    is_manual: false,
  }

  selectedContacts.value = [contactSelection]
  addAvailableContact({
    id: contactSelection.id ?? contactKey(contactSelection),
    phone: contactSelection.phone,
    bsuid: contactSelection.bsuid,
    username: contactSelection.username,
    name: contactSelection.name,
    label: buildContactLabel(contactSelection),
    custom_fields: contactSelection.custom_fields,
  })
  syncSelectAllState()
}

const ensureInitialContactInSelectedCampaign = async () => {
  const campaign = selectedCampaign.value
  const contact = props.initialContact

  if (!campaign || !contact) return

  // Sin teléfono no hay nada que buscar ni crear: los contactos se crean por
  // teléfono, y el cliente que solo tiene BSUID ya lo registró su chat.
  if (!props.createInitialContactOnCampaignSelect || !contact.phone) {
    addSelectedInitialContact(contact)
    return
  }

  try {
    const existingContact = await searchContact(
      {
        phone: contact.phone,
        campaign_id: campaign.id,
      },
      { showErrorToast: false },
    )

    const contactSelection = buildInitialContactSelection(existingContact, contact.custom_fields)
    addSelectedInitialContact(contactSelection)
    return
  } catch (error) {
    if (!isAxiosError(error) || error.response?.status !== 404) {
      throw error
    }
  }

  const createdContact = await createContact(
    {
      name: contact.name || contact.phone,
      phone: contact.phone,
      campaign_id: campaign.id,
      position: null,
      custom_fields: null,
    },
    { showSuccessToast: false },
  )

  const contactSelection = buildInitialContactSelection(createdContact, contact.custom_fields)
  addSelectedInitialContact(contactSelection)
}

const contactOptionToSelection = (contact: ContactOption): ContactSelection => ({
  id: contact.id,
  phone: contact.phone,
  bsuid: contact.bsuid,
  username: contact.username,
  name: contact.name,
  campaign_id: selectedCampaign.value?.id,
  is_manual: false,
  custom_fields: contact.custom_fields || [],
})

const fetchAllCampaignContacts = async (campaignId: string) => {
  const firstResponse = await getContacts({
    campaign_id: campaignId,
    page: 1,
    size: Math.max(contactsTotal.value, contactsPageSize),
  })

  const allContacts = [...firstResponse.data]
  const totalPages = firstResponse.meta.pages

  if (totalPages > 1) {
    const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2)
    const remainingResponses = await Promise.all(
      remainingPages.map((page) =>
        getContacts({
          campaign_id: campaignId,
          page,
          size: firstResponse.meta.size,
        }),
      ),
    )

    remainingResponses.forEach((response) => {
      allContacts.push(...response.data)
    })
  }

  return allContacts.filter(isReachable).map(toContactOption)
}

const handleSelectAllChange = async () => {
  if (props.singleSelection) return

  if (selectAllContacts.value) {
    if (!selectedCampaign.value) return

    const campaignId = selectedCampaign.value.id
    const requestId = ++latestSelectAllRequest
    const manualContacts = selectedContacts.value.filter((contact) => contact.is_manual)
    const manualKeys = new Set(manualContacts.map((contact) => contactKey(contact)))
    const visibleSelectedContactsByKey = new Map(
      selectedContacts.value
        .filter((contact) => !contact.is_manual)
        .map((contact) => [contactKey(contact), contact]),
    )

    availableContacts.value.forEach((contact) => {
      if (manualKeys.has(contactKey(contact))) return
      visibleSelectedContactsByKey.set(contactKey(contact), contactOptionToSelection(contact))
    })

    selectedAllContactKeys.value = new Set(visibleSelectedContactsByKey.keys())
    selectedContacts.value = [...manualContacts, ...visibleSelectedContactsByKey.values()]
    isSelectingAllContacts.value = true

    try {
      const allCampaignContacts = await fetchAllCampaignContacts(campaignId)

      if (requestId !== latestSelectAllRequest || selectedCampaign.value?.id !== campaignId) return

      selectedAllContactKeys.value = new Set(
        allCampaignContacts.map((contact) => contactKey(contact)),
      )
      selectedContacts.value = [
        ...manualContacts,
        ...allCampaignContacts
          .filter((contact) => !manualKeys.has(contactKey(contact)))
          .map(contactOptionToSelection),
      ]
      syncSelectAllState()
    } catch {
      if (requestId === latestSelectAllRequest) {
        selectAllContacts.value = false
      }
    } finally {
      if (requestId === latestSelectAllRequest) {
        isSelectingAllContacts.value = false
      }
    }
  } else {
    latestSelectAllRequest++
    selectedAllContactKeys.value = new Set()
    selectedContacts.value = selectedContacts.value.filter((c) => c.is_manual)
  }
}

const isContactSelected = (key: string) => selectedContactKeys.value.has(key)

const toContactOption = (contact: ContactListItemDto): ContactOption => ({
  id: contact.id,
  phone: contact.phone ?? '',
  bsuid: contact.bsuid ?? null,
  username: contact.username ?? null,
  name: contact.name,
  label: buildContactLabel(contact),
  custom_fields: contact.custom_fields || [],
})

const searchContacts = async (event: { query: string }) => {
  const query = event.query.trim()
  const requestId = ++latestSearchRequest

  if (!selectedCampaign.value || !query) {
    filteredContacts.value = availableContacts.value
    isLoadingContacts.value = false
    return
  }

  isLoadingContacts.value = true

  try {
    const response = await getContacts({
      campaign_id: selectedCampaign.value.id,
      search: query,
    })

    if (requestId !== latestSearchRequest) return

    filteredContacts.value = response.data.filter(isReachable).map(toContactOption)
  } catch {
    if (requestId === latestSearchRequest) filteredContacts.value = []
  } finally {
    if (requestId === latestSearchRequest) isLoadingContacts.value = false
  }
}

const addContactSelection = (contact: ContactOption) => {
  const contactSelection = {
    id: contact.id,
    phone: contact.phone,
    bsuid: contact.bsuid,
    username: contact.username,
    name: contact.name,
    campaign_id: selectedCampaign.value?.id,
    is_manual: false,
    custom_fields: contact.custom_fields || [],
  }

  if (props.singleSelection) {
    selectedContacts.value = [contactSelection]
    selectAllContacts.value = false
    selectedAllContactKeys.value = new Set()
    return
  }

  const exists = selectedContacts.value.find((c) => contactKey(c) === contactKey(contact))
  if (!exists) {
    selectedContacts.value = [...selectedContacts.value, contactSelection]
  }
}

const addAvailableContact = (contact: ContactOption) => {
  const exists = availableContacts.value.some(
    (availableContact) =>
      availableContact.id === contact.id || contactKey(availableContact) === contactKey(contact),
  )
  if (exists) return

  availableContacts.value = [contact, ...availableContacts.value]
}

const syncSelectAllState = () => {
  const selectedKeys = new Set(
    selectedContacts.value
      .filter((contact) => !contact.is_manual)
      .map((contact) => contactKey(contact)),
  )

  if (selectedAllContactKeys.value.size > 0) {
    selectAllContacts.value = [...selectedAllContactKeys.value].every((key) =>
      selectedKeys.has(key),
    )
    return
  }

  selectAllContacts.value =
    availableContacts.value.length > 0 &&
    availableContacts.value.every((contact) => selectedKeys.has(contactKey(contact)))
}

const handleContactSelect = (event: { value: ContactOption }) => {
  addAvailableContact(event.value)
  addContactSelection(event.value)
  syncSelectAllState()
  filteredContacts.value = availableContacts.value
  contactSearchQuery.value = ''
}

const handleAddManualContactClick = () => {
  if (isAddManualContactDisabled.value) return

  showManualContactsDialog.value = true
}

const toggleContactSelection = (contact: ContactOption) => {
  if (isContactSelected(contactKey(contact))) {
    removeContact(contactKey(contact))
    return
  }

  addContactSelection(contact)
  syncSelectAllState()
}

const handleManualContactsSave = (contacts: ContactSelection[]) => {
  if (props.singleSelection) {
    const contact = contacts[0]
    if (!contact) return

    selectedContacts.value = [
      {
        ...contact,
        campaign_id: selectedCampaign.value?.id,
        is_manual: true,
      },
    ]

    if (selectedCampaign.value) {
      void loadContactsPage(contactsPage.value)
    }
    return
  }

  const existingKeys = new Set(selectedContacts.value.map((contact) => contactKey(contact)))
  const contactsToAdd: ContactSelection[] = []

  contacts.forEach((contact) => {
    if (existingKeys.has(contactKey(contact))) return

    contactsToAdd.push({
      ...contact,
      campaign_id: selectedCampaign.value?.id,
      is_manual: true,
    })
    existingKeys.add(contactKey(contact))
  })

  if (contactsToAdd.length > 0) {
    selectedContacts.value = [...selectedContacts.value, ...contactsToAdd]
  }

  if (selectedCampaign.value) {
    emit('load-contacts', selectedCampaign.value.id)
  }
}

const removeContact = (key: string) => {
  if (selectAllContacts.value) {
    latestSelectAllRequest++
  }

  selectedContacts.value = selectedContacts.value.filter((c) => contactKey(c) !== key)
  syncSelectAllState()
}

const setAvailableContacts = (contacts: ContactOption[]) => {
  availableContacts.value = contacts
  filteredContacts.value = contacts
  syncSelectAllState()
}

watch(
  () => [whatsappCampaigns.value, props.defaultCampaignId] as const,
  async (campaigns) => {
    const [availableCampaigns, defaultCampaignId] = campaigns

    if (availableCampaigns.length === 0) {
      if (!selectedCampaign.value) return

      await handleCampaignChange(null)
      return
    }

    const selectedCampaignStillExists = selectedCampaign.value
      ? availableCampaigns.some((campaign) => campaign.id === selectedCampaign.value?.id)
      : false

    if (selectedCampaignStillExists && selectedCampaign.value?.id === defaultCampaignId) return

    if (
      props.createInitialContactOnCampaignSelect &&
      !defaultCampaignId &&
      availableCampaigns.length > 1 &&
      !selectedCampaignStillExists
    ) {
      return
    }

    const campaignToSelect =
      availableCampaigns.find((campaign) => campaign.id === defaultCampaignId) ??
      (selectedCampaignStillExists ? selectedCampaign.value : null) ??
      availableCampaigns[0]

    await handleCampaignChange(campaignToSelect.id)
  },
  { immediate: true },
)

watch(
  () => selectedContacts.value,
  (contacts) => {
    emit('update:contacts', contacts)
  },
)

watch(showManualContactsDialog, (visible) => {
  emit('manual-dialog-visibility', visible)
})

defineExpose({
  setAvailableContacts,
})
</script>

<style scoped>
:deep(.add-manual-contact-button:disabled) {
  cursor: not-allowed !important;
  pointer-events: auto;
}
</style>
